/**
 * The same dataset rendered with recharts — cinch-ops' incumbent charting lib —
 * so the AG Charts output can be judged side by side. Colors are the identical
 * resolved DS palette passed to AG Charts; recharts additionally reads the
 * axis/grid/tooltip colors straight from CSS vars, so it re-themes on dark mode
 * with no JS (the difference this whole lab is meant to surface).
 */
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { ChartDataset, ChartType } from "../datasets";

const AXIS_TICK = { fontSize: 11, fill: "hsl(var(--muted-foreground))" } as const;
const GRID_STROKE = "hsl(var(--border))";

function tooltipStyle() {
  return {
    background: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 8,
    color: "hsl(var(--popover-foreground))",
    fontSize: 12,
  } as const;
}

export function RechartsPanel({
  dataset,
  chartType,
}: {
  dataset: ChartDataset;
  chartType: ChartType;
}) {
  const { rows, series, colors, categoryKey, valueFormat } = dataset;
  const fmt = (v: number | string) => valueFormat(Number(v));
  const showLegend = series.length > 1 || dataset.kind === "category";

  return (
    <div className="h-[22rem] w-full">
      <ResponsiveContainer width="100%" height="100%">
        {dataset.kind === "category" && (chartType === "pie" || chartType === "donut") ? (
          <PieChart>
            <Pie
              data={rows}
              dataKey={series[0]?.key ?? "value"}
              nameKey={categoryKey}
              innerRadius={chartType === "donut" ? "55%" : 0}
              outerRadius="80%"
              paddingAngle={1}
              isAnimationActive={false}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip formatter={fmt} contentStyle={tooltipStyle()} />
            {showLegend && <Legend />}
          </PieChart>
        ) : chartType === "area" || chartType === "stacked-area" ? (
          <AreaChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey={categoryKey} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={fmt} width={56} />
            <Tooltip formatter={fmt} contentStyle={tooltipStyle()} />
            {showLegend && <Legend />}
            {series.map((s, i) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={colors[i % colors.length]}
                fill={colors[i % colors.length]}
                fillOpacity={0.35}
                strokeWidth={2}
                connectNulls={false}
                isAnimationActive={false}
                stackId={chartType === "stacked-area" ? "a" : undefined}
              />
            ))}
          </AreaChart>
        ) : chartType === "bar" || chartType === "stacked-bar" ? (
          <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey={categoryKey} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={fmt} width={56} />
            <Tooltip formatter={fmt} contentStyle={tooltipStyle()} cursor={{ fill: "hsl(var(--muted) / 0.4)" }} />
            {showLegend && <Legend />}
            {dataset.kind === "category" ? (
              <Bar dataKey={series[0]?.key ?? "value"} isAnimationActive={false} radius={[4, 4, 0, 0]}>
                {rows.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Bar>
            ) : (
              series.map((s, i) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.name}
                  fill={colors[i % colors.length]}
                  stackId={chartType === "stacked-bar" ? "a" : undefined}
                  isAnimationActive={false}
                  radius={chartType === "stacked-bar" ? undefined : [4, 4, 0, 0]}
                />
              ))
            )}
          </BarChart>
        ) : chartType === "horizontal-bar" ? (
          <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
            <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={fmt} />
            <YAxis
              type="category"
              dataKey={categoryKey}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={90}
            />
            <Tooltip
              formatter={fmt}
              contentStyle={tooltipStyle()}
              cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
            />
            {showLegend && <Legend />}
            {dataset.kind === "category" ? (
              <Bar dataKey={series[0]?.key ?? "value"} isAnimationActive={false} radius={[0, 4, 4, 0]}>
                {rows.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Bar>
            ) : (
              series.map((s, i) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.name}
                  fill={colors[i % colors.length]}
                  isAnimationActive={false}
                  radius={[0, 4, 4, 0]}
                />
              ))
            )}
          </BarChart>
        ) : (
          <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey={categoryKey} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={fmt} width={56} />
            <Tooltip formatter={fmt} contentStyle={tooltipStyle()} />
            {showLegend && <Legend />}
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={colors[i % colors.length]}
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
